import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  HostListener
} from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd
} from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from './store/http/translate.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { companyLanguagesActions } from '@modules/company-admin/store/actions';

@Component({
  selector: 'app-language-bar',
  template: `
  <div class="notranslate" translate="no">

    <!-- ================= TOP BUTTON ================= -->
    <div class="top-lang-btn" *ngIf="showTopButton">
      <button class="lang-toggle with-text" (click)="toggleTop($event)">
        <i class="text-gray fa-regular fa-globe"></i>

        <span class="selected-lang" *ngIf="active">
          {{ active.name }} ({{ active.code }})
        </span>
      </button>

      <ul *ngIf="topDropdownOpen" class="lang-menu">
        <li *ngFor="let l of languages"
            (click)="select(l, $event)"
            [class.active]="l.code === active?.code">
        
          <span class="label">{{ l.name }}</span>
        </li>
      </ul>
    </div>

    <!-- ================= SIDEBAR BUTTON (UNCHANGED) ================= -->
    <div class="side-lang-btn" *ngIf="showSideButton">
      <button class="lang-toggle square" (click)="toggleSide($event)">
        <i class="text-gray fa-regular fa-globe"></i>
      </button>

      <ul *ngIf="sideDropdownOpen" class="lang-menu">
        <li *ngFor="let l of languages"
            (click)="select(l, $event)"
            [class.active]="l.code === active?.code">
       
          <span class="label">{{ l.name }}</span>
        </li>
      </ul>
    </div>

  </div>
  `,
  styles: [`
    .top-lang-btn {
      position: fixed;
      top: 20px;
      left: 61%;
      transform: translateX(-50%);
      z-index: 999;
    }

    .side-lang-btn {
      position: fixed;
      top: 90px;
      right: 0;
      z-index: 999;
    }

    .lang-toggle {
      width: 45px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 5px;
      cursor: pointer;
      font-size: 20px;
      color: #374151;
    }

    .lang-toggle.with-text {
      width: auto;
      padding: 0 12px;
      gap: 8px;
    }

    .selected-lang {
      font-size: 14px;
      font-weight: 500;
    }

    .lang-toggle.square {
      width: 44px;
      height: 36px;
      border-radius: 3px 0 0 3px;
      border-top: 1px solid white;
      border-left: 1px solid white;
      border-bottom: 1px solid #ffffff;
      padding: 3px 0 2px 2px;
    }

    .lang-menu {
      position: absolute;
      top: 100%;
      right: 0;
      width: 170px;
      margin-top: 6px;
      padding: 4px 0;
      list-style: none;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.12);
    }

    .lang-menu li {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px;
      cursor: pointer;
    }

    .lang-menu li:hover,
    .lang-menu li.active {
      background: #f3f4f6;
    }

    .flag {
      width: 18px;
      height: 14px;
      object-fit: cover;
      border-radius: 2px;
    }

    .label {
      font-size: 14px;
    }

      @media (max-width: 700px) {
    .top-lang-btn {
      top: 17px;
      /*bottom: 85px;         move to bottom */
      left: 23%;
      transform: translateX(-50%);
    }

    .side-lang-btn {
      /* Below gallery sheet top (6.2rem) so globe stays reachable on mobile */
      top: auto;
      bottom: 6.5rem;
      right: 0;
    }

    .lang-menu {
      top: auto;
      bottom: 100%;        /* dropdown opens upward */
      margin-bottom: 6px;
      margin-top: 0;
      left: 0%;
    }
  }
  `]
})
export class LanguageBarComponent implements OnInit, OnDestroy, OnChanges {

  @Input() languages: any[] = [];
  active: any;

  showTopButton = false;
  showSideButton = true;

  topDropdownOpen = false;
  sideDropdownOpen = false;

  private sub = new Subscription();
  getLanguageSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private store$: Store,
    private actions$: Actions,
  ) {}

  ngOnInit(): void {

    this.sub.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {

          const path = (event.urlAfterRedirects || '').split('?')[0];

          // Path segments only — url.includes('interview') matched campaign slugs
          // like `new-campaign-interview` and forced the top text pill.
          const useTop =
            /\/interview(\/|$)/.test(path) ||
            path.includes('/story/create') ||
            path.includes('/inbound') ||
            path.includes('/testimonial/create') ||
            /\/request(\/|$)/.test(path) ||
            path.includes('/request/');

          this.showTopButton = useTop;
          this.showSideButton = !useTop;

          this.topDropdownOpen = false;
          this.sideDropdownOpen = false;

          this.cdr.markForCheck();
        })
    );

    this.getLanguageSubscription = this.actions$.pipe(
      ofType<companyLanguagesActions.LoadAllSuccess>(
        companyLanguagesActions.ActionTypes.PASS_LANGUAGE_SET
      )
    ).subscribe((action: any) => {
      this.languages = action.payload;
      this.setActiveLanguage();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['languages'] && this.languages?.length) {
      this.setActiveLanguage();
    }
  }

  private setActiveLanguage() {

    if (!this.languages?.length) return;
  
    // ✅ Read directly from real browser URL
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
  
    if (!urlLang) {
      this.setEnglish();
      return;
    }
  
    const found = this.languages.find(l => l.code === urlLang);
  
    if (found) {
      this.active = found;
      this.translate.setLanguage(found.code);
    } else {
      this.setEnglish();
    }
  }
  
  
  
  private setEnglish() {
    this.active = {
      name: 'English',
      code: 'en',
      flag: 'https://flagcdn.com/w40/gb.png'
    };
    this.translate.setLanguage('en');
  }
  
  

  toggleTop(event: Event) {
    event.stopPropagation();
    this.topDropdownOpen = !this.topDropdownOpen;
    if (this.topDropdownOpen) this.sideDropdownOpen = false;
  }

  toggleSide(event: Event) {
    event.stopPropagation();
    this.sideDropdownOpen = !this.sideDropdownOpen;
    if (this.sideDropdownOpen) this.topDropdownOpen = false;
  }
  select(lang: any, event: Event) {
    event.stopPropagation();
  
    if (!lang?.code) return;
  
    this.router.navigate([], {
      queryParams: { lang: lang.code },
      queryParamsHandling: 'merge'
    }).then(() => {
      // ONE reload after router updates URL
      window.location.reload();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.getLanguageSubscription) {
      this.getLanguageSubscription.unsubscribe();
    }
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    const clickedInside = (event.target as HTMLElement).closest(
      '.top-lang-btn, .side-lang-btn'
    );

    if (!clickedInside) {
      this.topDropdownOpen = false;
      this.sideDropdownOpen = false;
    }
  }
}
